// Presenter search input during Demo Mode.
//
// Identifying search and filter boxes (a patient name, an MBI, a practice) are
// marked `data-demo-search`. While Demo Mode is on they stay usable: the
// presenter can type and sees what they typed, because a term typed during
// the demo is the presenter's own choice, like saying it aloud. What must not
// appear is a term the presenter did NOT type during this demo: one typed
// before Demo Mode started, a saved filter restored from storage or the URL,
// or a late response that writes one back.
//
// This module tells those apart. It records the value of every trusted
// (user-generated) input or change event on a `data-demo-search` control
// while Demo Mode is on. demoProtectedFields and the grid filter guard then
// keep a value only if the presenter typed it during the current Demo Mode
// session; anything else is emptied. Programmatic writes never produce a
// trusted event, so a restore cannot whitelist itself.
//
// The record is reset every time Demo Mode starts, so a term typed in an
// earlier demo does not survive into the next one.

import { effectScope } from 'vue';
import store from '@/store';

const SEARCH_SELECTOR = '[data-demo-search]';
const MAX_TERMS = 500;
const terms = new Set();
let installed = false;
let trustUntrustedForTests = false;

function exact(value) {
  return String(value).trim().replace(/\s+/g, ' ').toLowerCase();
}

// Components often reformat what was typed (upper-case an MBI, drop the
// dashes from a phone number), so a second form ignores everything but
// letters and digits, in any script.
function compact(value) {
  return String(value).toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
}

function remember(value) {
  if (value == null) return;
  const forms = [exact(value), compact(value)].filter(Boolean);
  if (!forms.length) return;
  if (terms.size + forms.length > MAX_TERMS) terms.clear();
  forms.forEach((f) => terms.add(f));
}

function isSearchControl(el) {
  return Boolean(el?.matches?.(SEARCH_SELECTOR));
}

function onUserInput(event) {
  if (!event.isTrusted && !trustUntrustedForTests) return;
  if (store.getters['demoMode/isDemoMode'] === false) return;
  const el = event.target;
  if (!isSearchControl(el)) return;
  remember(el.value);
}

export function installDemoSearchInput() {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  // Capture phase: recorded before the app's own handler (v-model, a
  // debounced search) can write the value into a model.
  document.addEventListener('input', onUserInput, true);
  document.addEventListener('change', onUserInput, true);
  // Installed lazily from a component's setup, so the watcher gets its own
  // detached scope: otherwise it would stop when that component unmounts, and
  // terms typed in one demo would survive into the next.
  effectScope(true).run(() => {
    store.watch(
      (state, getters) => getters['demoMode/isDemoMode'],
      () => terms.clear(),
      { flush: 'sync' }
    );
  });
}

// True when every non-empty string in `value` (a string or an array of
// strings) is something the presenter typed into a search box during this
// Demo Mode session. Anything else (numbers, objects, untyped strings) is not.
export function presenterTyped(value) {
  const values = Array.isArray(value) ? value : [value];
  const strings = values.filter((v) => v != null && v !== '');
  if (!strings.length) return false;
  return strings.every((v) => {
    if (typeof v !== 'string') return false;
    const e = exact(v);
    const c = compact(v);
    return (e && terms.has(e)) || (c && terms.has(c));
  });
}

// Tests run in jsdom, where every dispatched event is untrusted. Real trusted
// typing is covered by the browser suites.
export function trustUntrustedInputForTests(on) {
  trustUntrustedForTests = Boolean(on);
}

export function resetDemoSearchInputForTests() {
  terms.clear();
}
